/**
 * Toolbar for the Defense Technology Intelligence graph: entity search that
 * fans out to BOTH APIs, search-within-graph, back/forward graph navigation,
 * fit/reset controls and expansion result-size selection.
 */
import { FC, useState } from 'react';
import {
  Autocomplete,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';

export type ResultSize = 25 | 50 | 100 | 200;

interface IntelligenceGraphToolbarProps {
  onSearch: (query: string) => void;
  searching?: boolean;
  resultSize: ResultSize;
  onResultSizeChange: (size: ResultSize) => void;
  /** Labels of nodes currently in the graph, for search-within-graph. */
  graphNodeLabels: { id: string; label: string }[];
  onLocateNode: (nodeId: string) => void;
  canGoBack: boolean;
  canGoForward: boolean;
  onBack: () => void;
  onForward: () => void;
  onFit: () => void;
  onReset: () => void;
  initialQuery?: string;
}

export const IntelligenceGraphToolbar: FC<IntelligenceGraphToolbarProps> = ({
  onSearch,
  searching,
  resultSize,
  onResultSizeChange,
  graphNodeLabels,
  onLocateNode,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
  onFit,
  onReset,
  initialQuery,
}) => {
  const [query, setQuery] = useState(initialQuery ?? '');

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ px: 2, py: 1, flexWrap: 'wrap', rowGap: 1 }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Icon icon="mdi:radar" width={22} color="#dc2626" />
        <Typography variant="subtitle1" fontWeight={700} noWrap>
          Defense Technology Intelligence
        </Typography>
      </Stack>

      <TextField
        size="small"
        placeholder="Search organization, person, technology, PLA unit…"
        value={query}
        disabled={searching}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && query.trim()) onSearch(query.trim());
        }}
        sx={{ flex: 1, minWidth: 280 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon icon="mdi:magnify" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Search both Publications and PLA Contracts APIs">
                <span>
                  <IconButton
                    size="small"
                    disabled={searching || !query.trim()}
                    onClick={() => onSearch(query.trim())}
                  >
                    <Icon
                      icon={searching ? 'mdi:loading' : 'mdi:send'}
                      className={searching ? 'spin' : undefined}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />

      <Autocomplete
        size="small"
        sx={{ width: 230 }}
        options={graphNodeLabels}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.label
        }
        onChange={(_, option) => {
          if (option && typeof option !== 'string') onLocateNode(option.id);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Find in graph…"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="mdi:map-search-outline" width={16} />
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <TextField
        select
        size="small"
        label="Results"
        value={resultSize}
        onChange={(e) =>
          onResultSizeChange(Number(e.target.value) as ResultSize)
        }
        sx={{ width: 90 }}
      >
        {[25, 50, 100, 200].map((size) => (
          <MenuItem key={size} value={size}>
            {size}
          </MenuItem>
        ))}
      </TextField>

      <Divider orientation="vertical" flexItem />

      <Box>
        <Tooltip title="Back">
          <span>
            <IconButton size="small" disabled={!canGoBack} onClick={onBack}>
              <Icon icon="mdi:arrow-left" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Forward">
          <span>
            <IconButton
              size="small"
              disabled={!canGoForward}
              onClick={onForward}
            >
              <Icon icon="mdi:arrow-right" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Fit graph">
          <IconButton size="small" onClick={onFit}>
            <Icon icon="mdi:fit-to-screen-outline" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset graph">
          <IconButton size="small" onClick={onReset}>
            <Icon icon="mdi:restart" />
          </IconButton>
        </Tooltip>
      </Box>
    </Stack>
  );
};
